import type { FC } from 'react'
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import Paper from 'components/@extended/Paper'

interface SmartImportEmployeeRewardsSummaryProps {
  totalEmployees: number
  successfulEmployees: number
  failedEmployees: number
  skippedRows: number
  periodLabel?: string
  onViewEmployeeRewards: () => void
  onBackToMatching?: () => void
}

const SmartImportEmployeeRewardsSummary: FC<SmartImportEmployeeRewardsSummaryProps> = ({
  totalEmployees,
  successfulEmployees,
  failedEmployees,
  skippedRows,
  periodLabel,
  onViewEmployeeRewards,
  onBackToMatching,
}) => {
  const theme = useTheme()
  const hasFailures = failedEmployees > 0

  return (
    <Paper sx={{ p: { xs: 2.5, md: 3 }, width: '100%' }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {hasFailures ? (
            <ExclamationCircleOutlined style={{ color: theme.palette.warning.main, fontSize: 22 }} />
          ) : (
            <CheckCircleOutlined style={{ color: theme.palette.success.main, fontSize: 22 }} />
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Employee rewards import complete
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created rewards for {successfulEmployees} of {totalEmployees} matched employees.
              {failedEmployees > 0 ? ` ${failedEmployees} failed.` : ''}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Period: {periodLabel || 'Selected period'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Skipped rows: {skippedRows}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          {hasFailures && onBackToMatching && (
            <Button variant="outlined" onClick={onBackToMatching}>
              Back to matching
            </Button>
          )}
          <Button variant="contained" onClick={onViewEmployeeRewards}>
            View employee rewards
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default SmartImportEmployeeRewardsSummary
